/**
 * What the worker keeps of a crown GeoPackage and how it serves it: the polygons and their
 * centroids, each carrying only the properties the map styles and the popup shows, indexed as
 * tiles. Centroids are drawn below CROWN_POLYGON_MIN_ZOOM, outlines from there up.
 */
import type { Feature, FeatureCollection, Geometry, Position } from 'geojson';
import { GeoJSONVT, type GeoJSONVTTile } from '@maplibre/geojson-vt';
import { fromGeojsonVt } from '@maplibre/vt-pbf';
import type { Threshold } from '@/features/reports/trees/config';
import { treeExtras } from '@/features/reports/trees/extras';
import { baseStats, withMaturity } from '@/features/reports/trees/stats';
import { CROWN_LAYERS, CROWN_POLYGON_MIN_ZOOM, CROWN_TILE_MAX_ZOOM, TREE_EXTRAS, TREE_FIELDS } from './layers';

export type Layer = FeatureCollection<Geometry, Record<string, unknown>>;

export { CROWN_LAYERS, CROWN_POLYGON_MIN_ZOOM, CROWN_TILE_MAX_ZOOM };

/** Properties kept on every tile feature; the rest of forun's columns stay in the worker. */
const KEPT = new Set<string>(['id', 'fid', TREE_FIELDS.genus, TREE_FIELDS.genusCzech, TREE_FIELDS.height.prop, TREE_FIELDS.dbh.prop, TREE_FIELDS.volume.prop]);

/** Encoded tiles kept per layer; the viewport needs a few dozen, a pan a few dozen more. */
const ENCODED_TILE_CACHE = 256;

/**
 * Below the polygon zoom a dot is a pixel or less, so each zoom level further out draws every
 * second point of the one above it: the density reads the same and a tile stays small.
 */
export function pointStride(z: number): number {
  return 2 ** Math.max(0, CROWN_POLYGON_MIN_ZOOM - 1 - z);
}

export interface CrownLayer {
  /** One point per crown with the kept properties: what the page computes statistics from. */
  points: Layer;
  /** Area of the convex hull of every crown vertex, in hectares (the stand area without a boundary). */
  hullHa: number;
  tile(z: number, x: number, y: number): Uint8Array;
  /** Recompute `_mature`/`_stratum` under other thresholds; tiles served afterwards carry them. */
  retag(thresholds: Threshold[]): void;
}

function centroid(g: Geometry): Position | null {
  if (g.type === 'Point') return g.coordinates;
  const ring = g.type === 'Polygon' ? g.coordinates[0] : g.type === 'MultiPolygon' ? g.coordinates[0]?.[0] : undefined;
  if (!ring?.length) return null;
  const closed = ring.length > 1 && ring[0]![0] === ring.at(-1)![0] && ring[0]![1] === ring.at(-1)![1];
  const pts = closed ? ring.slice(0, -1) : ring;
  return [pts.reduce((s, p) => s + p[0]!, 0) / pts.length, pts.reduce((s, p) => s + p[1]!, 0) / pts.length];
}

const slim = (p: Record<string, unknown>): Record<string, unknown> => Object.fromEntries(Object.entries(p).filter(([k]) => KEPT.has(k)));

function tag(features: Feature<Geometry, Record<string, unknown>>[], thresholds: Threshold[], points: Layer): void {
  const extras = treeExtras(withMaturity(baseStats(points, null), thresholds));
  features.forEach((f, i) => {
    const x = extras.get(String(f.id ?? i + 1));
    // In place: the tile index holds these very objects as its tags.
    f.properties[TREE_EXTRAS.mature] = x?.mature ?? false;
    f.properties[TREE_EXTRAS.stratum] = x?.stratum ?? null;
  });
}

export function buildCrownLayer(fc: Layer, thresholds: Threshold[] = []): CrownLayer {
  const polygons: Layer = { type: 'FeatureCollection', features: [] };
  const points: Layer = { type: 'FeatureCollection', features: [] };
  fc.features.forEach((f, i) => {
    const c = centroid(f.geometry);
    if (!c) return;
    const id = f.id ?? i + 1;
    const properties = slim(f.properties ?? {});
    // One properties object per crown, shared by its point and its polygon, so a retag reaches both.
    polygons.features.push({ type: 'Feature', id, geometry: f.geometry, properties });
    points.features.push({ type: 'Feature', id, geometry: { type: 'Point', coordinates: c }, properties });
  });
  const hullHa = baseStats(fc, null).area.ha;
  tag(points.features, thresholds, points);

  let polygonIndex: GeoJSONVT | null = null;
  // One point index per thinning stride, built when a zoom first asks for it.
  const pointIndexes = new Map<number, GeoJSONVT>();
  const encoded = new Map<string, Uint8Array>();
  const reset = () => {
    // The whole stand sits in a few z10 tiles; deeper tiles are cut on demand and cached by geojson-vt.
    polygonIndex = new GeoJSONVT(polygons, { maxZoom: CROWN_TILE_MAX_ZOOM, indexMaxZoom: 10, indexMaxPoints: 0, tolerance: 3, extent: 4096, buffer: 64 });
    pointIndexes.clear();
    encoded.clear();
  };
  const pointIndexFor = (z: number): GeoJSONVT => {
    const stride = pointStride(z);
    let idx = pointIndexes.get(stride);
    if (!idx) {
      const subset: Layer = stride === 1 ? points : { type: 'FeatureCollection', features: points.features.filter((_, i) => i % stride === 0) };
      idx = new GeoJSONVT(subset, { maxZoom: CROWN_POLYGON_MIN_ZOOM, indexMaxZoom: 10, indexMaxPoints: 0, tolerance: 0, extent: 4096, buffer: 64 });
      pointIndexes.set(stride, idx);
    }
    return idx;
  };
  reset();

  return {
    points,
    hullHa,
    tile(z, x, y) {
      const key = `${z}/${x}/${y}`;
      const hit = encoded.get(key);
      // A copy: the worker transfers what it is given, which would leave the cached bytes detached.
      if (hit) return hit.slice();
      const layers: Record<string, GeoJSONVTTile> = {};
      const t = z < CROWN_POLYGON_MIN_ZOOM ? pointIndexFor(z).getTile(z, x, y) : polygonIndex!.getTile(z, x, y);
      if (t) layers[z < CROWN_POLYGON_MIN_ZOOM ? CROWN_LAYERS.points : CROWN_LAYERS.polygons] = t;
      const bytes = fromGeojsonVt(layers, { version: 2, extent: 4096 });
      if (encoded.size >= ENCODED_TILE_CACHE) encoded.delete(encoded.keys().next().value!);
      encoded.set(key, bytes);
      return bytes.slice();
    },
    retag(thresholds) {
      tag(points.features, thresholds, points);
      reset(); // drops every cached tile, which carries the old tags
    },
  };
}
